import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, PlayCircle, ChevronDown, ChevronRight, HelpCircle, X, History, RefreshCw, Calendar, Download, FileBarChart, CheckSquare } from 'lucide-react';
import { startPersonalAssessment } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import AssessmentModal from '../components/AssessmentModal';
import QuestionModal from '../components/QuestionModal';
import { PERSONAL_CATEGORY_DESCRIPTIONS, PERSONAL_CATEGORY_KEY_MAP } from '../utils/categoryDescriptions';
import { usePersonalGapAnalysisData } from '../hooks/usePersonalGapAnalysisData';
import ViewerBanner from '../components/ViewerBanner';
import {
  getGapZone,
  getScoreColor,
  getScoreDarkColor,
  getScoreLightBg,
  getScoreTextColor,
  getScoreBadgeColor,
  getCategoryBackgroundColor,
  getGapZoneLabel,
  getQuestionsByGapZone as getQuestionsByGapZoneUtil,
  getGapZoneInfo,
  getAssessmentType,
  isFirstInGroup,
  isLastInGroup
} from '../utils/personalGapAnalysisUtils';
import { CATEGORY_DISPLAY_NAMES, GAP_ZONE_ORDER, ASSESSMENT_TYPE_GROUPS } from '../utils/personalGapAnalysisConstants';
import logger from '../utils/logger';
import ScoringGuideModal from '../components/personal/ScoringGuideModal';
import StartAssessmentModal from '../components/personal/StartAssessmentModal';
import AssessmentSummaryModal from '../components/personal/AssessmentSummaryModal';
import CategoryHelpModal from '../components/personal/CategoryHelpModal';
import PersonalScoringCriteriaModal from '../components/personal/PersonalScoringCriteriaModal';
import GapDistributionChart from '../components/personal/GapDistributionChart';
import './GapAnalysis.css';

// Define impact domains in correct order: Financial, Wellbeing, Spiritual
const IMPACT_DOMAINS = [
  { key: 'Financial', field: 'impacts_personal_financial' },
  { key: 'Wellbeing', field: 'impacts_personal_relational' },
  { key: 'Spiritual', field: 'impacts_personal_spiritual' }
];

const PersonalGapAnalysis = () => {
  const navigate = useNavigate();
  const { isViewer } = usePermissions();

  // Use custom hook for all state and data management
  const {
    personalAssessment,
    allQuestions,
    loading,
    showAssessmentModal,
    expandedCategory,
    selectedQuestion,
    hoveredGapZone,
    selectedGapFilter,
    showScoringGuide,
    isScoringGuideCollapsed,
    showHistoryModal,
    assessmentHistory,
    viewingHistoricalAssessment,
    showFullQuestionText,
    categoryAnimationKeys,
    overallProgressAnimationKey,
    downloadingPDF,
    activityTypeFilter,
    personalImpactFilter,
    showActivityTypeHelp,
    showImpactTypeHelp,
    showScoringGuideHelp,
    showCDTHelp,
    showSummaryModal,
    summaryData,
    loadingSummary,
    initialQuestionId,
    isChartHovered,
    selectedAssessmentTypes,
    showStartModal,
    showCategoryHelp,
    categoryOrder,
    setShowAssessmentModal,
    setExpandedCategory,
    setSelectedQuestion,
    setHoveredGapZone,
    setSelectedGapFilter,
    setShowScoringGuide,
    setIsScoringGuideCollapsed,
    setShowHistoryModal,
    setViewingHistoricalAssessment,
    setShowFullQuestionText,
    setCategoryAnimationKeys,
    setActivityTypeFilter,
    setPersonalImpactFilter,
    setShowActivityTypeHelp,
    setShowImpactTypeHelp,
    setShowScoringGuideHelp,
    setShowCDTHelp,
    setShowSummaryModal,
    setInitialQuestionId,
    setIsChartHovered,
    setShowStartModal,
    setShowCategoryHelp,
    loadAssessment,
    getAnsweredQuestions,
    getCategoryQuestions,
    getAnswer,
    handleModalSave,
    handleQuestionEdit,
    handleQuestionSave,
    handleStartAssessment,
    handleConfirmStartAssessment,
    toggleAssessmentType,
    handleRetakeAssessment,
    handleViewHistory,
    handleLoadHistoricalAssessment,
    handleBackToCurrent,
    handleDownloadPDF,
    handleViewSummary,
    handleGapZoneClick,
    toggleCategory
  } = usePersonalGapAnalysisData();

  // Wrap getQuestionsByGapZone utility to match existing API
  const getQuestionsByGapZone = () => {
    return getQuestionsByGapZoneUtil(getAnsweredQuestions());
  };

  // Memoize filtered questions to keep stable array reference across re-renders.
  // Must be before early returns to satisfy Rules of Hooks (hooks cannot come after conditional returns).
  const assessmentModalQuestions = useMemo(() => {
    const selectedCategories = selectedAssessmentTypes.flatMap(type =>
      ASSESSMENT_TYPE_GROUPS[type]?.categories || []
    );
    return allQuestions.filter(q => selectedCategories.includes(q.category));
  }, [allQuestions, selectedAssessmentTypes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading personal assessment...</div>
      </div>
    );
  }

  if (!personalAssessment) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Personal Assessment Found</h2>
          {!isViewer && (
            <button
              onClick={async () => {
                await startPersonalAssessment();
                loadAssessment();
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Start Personal Assessment
            </button>
          )}
          {isViewer && (
            <p className="text-gray-600 mt-2">
              No assessment data available to view yet.
            </p>
          )}
        </div>
      </div>
    );
  }

  const answeredQuestions = getAnsweredQuestions();
  const progressPercent = allQuestions.length > 0
    ? (personalAssessment.answered_questions / allQuestions.length) * 100
    : 0;

  const categoryScores = personalAssessment.category_scores || {};

  // DEBUG: Log data loading status
  console.log('Personal Gap Assessment - Data Status:', {
    isViewer,
    allQuestionsLoaded: allQuestions.length,
    answeredQuestionsCount: answeredQuestions.length,
    assessmentExists: !!personalAssessment,
    categoryScoresCount: Object.keys(categoryScores).length,
    expandedCategories: expandedCategory
  });

  // Calculate scores for each filter category (must be after answeredQuestions is defined)
  const calculateFilterScores = (mapping) => {
    const scores = {};

    Object.keys(mapping).forEach(filterKey => {
      const questionIds = mapping[filterKey];

      // Match questions by question_id (now using string IDs like 'FIN-001', 'REV-002', etc.)
      const matchingResponses = answeredQuestions.filter(q => {
        return questionIds.includes(q.question_id);
      });

      // Calculate average score, excluding N/A (0) responses
      const validResponses = matchingResponses.filter(r => r.answer_value !== 0);
      if (validResponses.length > 0) {
        const totalScore = validResponses.reduce((sum, r) => sum + (r.score || 0), 0);
        scores[filterKey] = totalScore / validResponses.length;
      } else {
        scores[filterKey] = 0;
      }
    });

    return scores;
  };

  // Calculate activity type scores by filtering questions directly
  const calculateActivityTypeScores = () => {
    const scores = {};
    const activityTypes = ['De-Risking Activity', 'Strategy Activity', 'Efficiency Activity', 'Growth Activity', 'Culture Activity'];

    activityTypes.forEach(activityType => {
      // Get answered questions and join with question metadata
      const answeredTypeQuestions = answeredQuestions.filter(response => {
        // Find the matching question to get activity_type
        const question = allQuestions.find(q => q.question_id === response.question_id);
        return question && question.activity_type === activityType && response.answer_value !== 0;
      });

      if (answeredTypeQuestions.length > 0) {
        const totalScore = answeredTypeQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
        scores[activityType] = totalScore / answeredTypeQuestions.length;
      } else {
        scores[activityType] = 0;
      }
    });

    return scores;
  };

  // Calculate impact domain scores
  const calculateImpactDomainScores = () => {
    const scores = {};

    IMPACT_DOMAINS.forEach(domain => {
      // Get answered questions and join with question metadata
      const domainQuestions = answeredQuestions.filter(response => {
        // Find the matching question to get impact flags
        const question = allQuestions.find(q => q.question_id === response.question_id);
        return question && question[domain.field] === true && response.answer_value !== 0;
      });

      if (domainQuestions.length > 0) {
        const totalScore = domainQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
        scores[domain.key] = totalScore / domainQuestions.length;
      } else {
        scores[domain.key] = 0;
      }
    });

    return scores;
  };

  const activityTypeScores = calculateActivityTypeScores();
  const impactDomainScores = calculateImpactDomainScores();

  // Frontend fallback: Calculate real category scores if backend returns same score for all
  const calculateCategoryScores = () => {
    const scores = {};
    const answered = getAnsweredQuestions();
    
    categoryOrder.forEach(catKey => {
      const categoryQuestions = allQuestions.filter(q => q.category === catKey);
      const categoryAnswers = answered.filter(a => {
        const question = categoryQuestions.find(q => q.question_id === a.question_id);
        return question && a.answer_value !== 0; // Exclude N/A answers
      });
      
      if (categoryAnswers.length > 0) {
        const totalScore = categoryAnswers.reduce((sum, a) => sum + a.score, 0);
        scores[catKey] = totalScore / categoryAnswers.length;
      } else {
        scores[catKey] = 0;
      }
    });
    
    return scores;
  };
  
  // Check if backend returned all same scores (indicating it's just using overall score)
  const backendScoreValues = Object.values(categoryScores);
  const allSameScore = backendScoreValues.length > 0 && 
    backendScoreValues.every(score => score === backendScoreValues[0]);
  
  // Use calculated scores if backend scores are all the same
  const finalCategoryScores = allSameScore ? calculateCategoryScores() : categoryScores;

  logger.debug('Personal assessment category scores calculated', {
    backendScores: categoryScores,
    allSameScore,
    finalScores: finalCategoryScores
  });

  // Use ordered category list with fallback for safety
  const backendCategoryKeys = Object.keys(finalCategoryScores);

  // Try to maintain correct order by filtering categoryOrder
  let orderedKeys = categoryOrder.filter(key => {
    return finalCategoryScores.hasOwnProperty(key);
  });

  // DEBUG: Log category information
  console.log('Personal Gap - Category Debug:', {
    categoryOrderLength: categoryOrder.length,
    backendCategoryKeysLength: backendCategoryKeys.length,
    orderedKeysLength: orderedKeys.length,
    categoryOrderSample: categoryOrder.slice(0, 5),
    backendKeysSample: backendCategoryKeys.slice(0, 5)
  });

  // If no matches, use backend keys as fallback (even if wrong order)
  const finalCategoryKeys = orderedKeys.length > 0 ? orderedKeys : backendCategoryKeys;

  // Filter questions if gap zone, activity type, or intangible asset is selected
  const getFilteredQuestions = (categoryKey) => {
    let categoryQuestions = getCategoryQuestions(categoryKey);

    // Apply gap zone filter
    if (selectedGapFilter) {
      // Special handling for N/A filter
      if (selectedGapFilter === 'n-a') {
        categoryQuestions = categoryQuestions.filter(q => {
          const answer = getAnswer(q.question_id);
          return answer && answer.answer_value === 0;
        });
      } else {
        // Normal gap zone filtering
        categoryQuestions = categoryQuestions.filter(q => {
          const answer = getAnswer(q.question_id);
          if (!answer) return false;
          return getGapZone(answer.score) === selectedGapFilter;
        });
      }
    }

    // Apply activity type filter
    if (activityTypeFilter) {
      categoryQuestions = categoryQuestions.filter(q => {
        return q.activity_type === activityTypeFilter;
      });
    }

    // Apply personal impact domain filter
    if (personalImpactFilter) {
      categoryQuestions = categoryQuestions.filter(q => {
        if (personalImpactFilter === 'Financial') {
          return q.impacts_personal_financial === true;
        } else if (personalImpactFilter === 'Wellbeing') {
          return q.impacts_personal_relational === true;
        } else if (personalImpactFilter === 'Spiritual') {
          return q.impacts_personal_spiritual === true;
        }
        return true;
      });
    }

    return categoryQuestions;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Viewer Banner */}
      <ViewerBanner />

      {/* Historical Assessment Banner */}
      {viewingHistoricalAssessment && (
        <div className="mb-4 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="text-amber-600" size={24} />
            <div>
              <h3 className="font-semibold text-amber-900">Viewing Historical Assessment</h3>
              <p className="text-sm text-amber-700">
                Completed on {new Date(personalAssessment.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button
            onClick={handleBackToCurrent}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
          >
            <RefreshCw size={18} />
            <span>Back to Current</span>
          </button>
        </div>
      )}

      {/* Header - Redesigned */}
      <div className="mb-6">
        {/* Title Section */}
        <div className="mb-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Personal Gap Assessment</h1>
          <p className="text-lg text-gray-600">
            Assess your financial, wellbeing, and spiritual readiness for a successful business exit and life transition
          </p>
        </div>

        {/* Action Buttons - Organized in Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button
            onClick={handleViewHistory}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition shadow-sm"
          >
            <History size={20} />
            <span className="font-medium">History</span>
          </button>

          {personalAssessment && personalAssessment.answered_questions > 0 && (
            <>
              <button
                onClick={() => navigate('/task-manager')}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition shadow-sm"
              >
                <CheckSquare size={20} />
                <span className="font-medium">View Tasks</span>
              </button>

              <button
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-green-200 text-green-700 rounded-lg hover:bg-green-50 hover:border-green-300 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={20} />
                <span className="font-medium">{downloadingPDF ? 'Generating...' : 'Download'}</span>
              </button>
            </>
          )}

          {!viewingHistoricalAssessment && personalAssessment && personalAssessment.answered_questions > 0 && !isViewer && (
            <button
              onClick={handleRetakeAssessment}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-orange-200 text-orange-700 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition shadow-sm"
            >
              <RefreshCw size={20} />
              <span className="font-medium">Retake</span>
            </button>
          )}

          <button
            onClick={() => setShowScoringGuide(true)}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition shadow-sm"
          >
            <HelpCircle size={20} />
            <span className="font-medium">Scoring Guide</span>
          </button>
        </div>
      </div>

      {/* Show "Start Assessment" if no answers yet */}
      {personalAssessment.answered_questions === 0 && !isViewer && (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-8 text-center mb-6">
          <div className="mb-4">
            <FileText size={48} className="mx-auto text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ready to assess your personal exit readiness?
          </h2>
          <p className="text-gray-600 mb-6">
            Complete our comprehensive {allQuestions.length}-question assessment to evaluate your financial, wellbeing, and spiritual preparedness
          </p>
          <button
            onClick={handleStartAssessment}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
          >
            <PlayCircle size={20} className="mr-2" />
            Start Personal Assessment</button>
        </div>
      )}

      {/* Show Dashboard if assessment has answers */}
      {personalAssessment.answered_questions > 0 && (
        <div className="space-y-6">
          {/* Overall Score Card - Redesigned */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-8 pt-6 pb-8 text-white relative overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>

              <div className="relative">
                {/* Title and Info */}
                <div className="mb-6">
                  <h2 className="text-3xl font-bold mb-2">Overall Assessment Score</h2>
                  <div className="flex items-center space-x-4 text-blue-100">
                    <span className="text-sm font-medium">
                      {personalAssessment.answered_questions} of {allQuestions.length} questions answered
                    </span>
                    <span className="text-sm">•</span>
                    <span className="text-sm font-medium">{Math.round(progressPercent)}% complete</span>
                  </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  {/* Score Display */}
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <div className="text-center">
                      <div className="text-6xl font-bold mb-3 leading-none">
                        {Math.round(personalAssessment.overall_score)}%
                      </div>
                      <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold shadow-lg ${
                        personalAssessment.overall_score > 86 ? 'bg-[#c49e73] text-[#8b6f47]' :
                        personalAssessment.overall_score > 72 ? 'bg-[#a7d5a8] text-green-800' :
                        personalAssessment.overall_score > 57 ? 'bg-[#b8d4e8] text-blue-800' :
                        personalAssessment.overall_score > 43 ? 'bg-[#f4ebb0] text-yellow-800' :
                        personalAssessment.overall_score > 28 ? 'bg-[#f5d7b3] text-orange-800' : 'bg-[#f5c9c9] text-red-800'
                      }`}>
                        {personalAssessment.overall_score > 86 ? 'No Gaps' :
                         personalAssessment.overall_score > 72 ? 'Minor Gaps' :
                         personalAssessment.overall_score > 57 ? 'Considerable Gaps' :
                         personalAssessment.overall_score > 43 ? 'Critical Gaps' :
                         personalAssessment.overall_score > 28 ? 'Very Critical Gaps' : 'Extremely Critical'}
                      </div>
                      <div className="mt-3 text-xs text-blue-100 font-medium">Personal Gap Assessment Score</div>
                    </div>
                  </div>

                  {/* Progress & Actions */}
                  <div className="md:col-span-2 space-y-4">
                    {/* Progress Bar */}
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold">Assessment Progress</span>
                        <span className="text-sm font-bold">{Math.round(progressPercent)}%</span>
                      </div>
                      <div className="relative bg-white/20 rounded-full h-4 overflow-hidden shadow-inner">
                        <div
                          key={`progress-${overallProgressAnimationKey}`}
                          className="h-full rounded-full bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg"
                          style={{
                            width: 0,
                            animation: `growWidth 2000ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                            animationFillMode: 'forwards',
                            '--final-width': `${progressPercent}%`
                          }}
                        ></div>
                      </div>
                      <div className="mt-2 text-xs text-blue-100">
                        {personalAssessment.answered_questions < allQuestions.length
                          ? `${allQuestions.length - personalAssessment.answered_questions} questions remaining`
                          : 'Assessment complete!'}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      {personalAssessment.answered_questions < allQuestions.length && !viewingHistoricalAssessment ? (
                        <button
                          onClick={handleStartAssessment}
                          className="flex items-center justify-center space-x-2 px-5 py-3 bg-white text-blue-600 rounded-xl hover:bg-blue-50 hover:scale-105 font-bold transition-all shadow-lg"
                        >
                          <PlayCircle size={18} />
                          <span>Continue</span>
                        </button>
                      ) : (
                        <div className="flex items-center justify-center px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                          <span className="text-sm font-semibold text-white">Assessment Complete</span>
                        </div>
                      )}

                      <button
                        onClick={handleViewSummary}
                        className="flex items-center justify-center space-x-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-105 font-bold transition-all shadow-lg"
                      >
                        <FileBarChart size={18} />
                        <span>View Summary</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid - Categories Left, Charts Right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
            {/* LEFT SIDE - Category Scores with Expandable Questions */}
            <div className="lg:col-span-3 space-y-3">
              {/* Header with Filters */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">Category Breakdown</h3>
                  <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showFullQuestionText}
                      onChange={(e) => setShowFullQuestionText(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Show full question text</span>
                  </label>
                </div>

                {/* Scoring Criteria Help Button */}
                <div className="mb-3">
                  <button
                    onClick={() => setShowCDTHelp(true)}
                    className="inline-flex items-center space-x-1.5 text-blue-600 hover:text-blue-800 transition px-2 py-1 rounded-md hover:bg-blue-50"
                    title="Learn more about Clear, Documented, & Transferable"
                  >
                    <HelpCircle size={16} />
                    <span className="text-xs font-medium">What does "Clear, Documented, & Transferable" mean?</span>
                  </button>
                </div>

                {/* Filter Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Activity Type Filter */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Filter by Activity Type
                      </label>
                      <button
                        onClick={() => setShowActivityTypeHelp(true)}
                        className="text-blue-600 hover:text-blue-800 transition"
                        title="Learn more about Activity Types"
                      >
                        <HelpCircle size={16} />
                      </button>
                    </div>
                    <select
                      value={activityTypeFilter || ''}
                      onChange={(e) => {
                        const newFilter = e.target.value || null;
                        setActivityTypeFilter(newFilter);

                        // Auto-expand categories with matching questions
                        if (newFilter || personalImpactFilter) {
                          const categoriesToExpand = [];
                          finalCategoryKeys.forEach(catKey => {
                            const categoryQuestions = getCategoryQuestions(catKey);
                            const hasMatchingQuestions = categoryQuestions.some(q => {
                              // Check activity type
                              if (newFilter && q.activity_type !== newFilter) return false;
                              // Check personal impact domain
                              if (personalImpactFilter) {
                                if (personalImpactFilter === 'Financial' && !q.impacts_personal_financial) return false;
                                if (personalImpactFilter === 'Wellbeing' && !q.impacts_personal_relational) return false;
                                if (personalImpactFilter === 'Spiritual' && !q.impacts_personal_spiritual) return false;
                              }
                              return true;
                            });
                            if (hasMatchingQuestions) {
                              categoriesToExpand.push(catKey);
                            }
                          });
                          setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : []);
                        } else {
                          setExpandedCategory([]);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">All Activities</option>
                      <option value="De-Risking Activity">De-Risking Activity</option>
                      <option value="Strategy Activity">Strategy Activity</option>
                      <option value="Efficiency Activity">Efficiency Activity</option>
                      <option value="Growth Activity">Growth Activity</option>
                      <option value="Culture Activity">Culture Activity</option>
                    </select>
                  </div>

                  {/* Personal Impact Domain Filter */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Filter by Impact Domain
                      </label>
                      <button
                        onClick={() => setShowImpactTypeHelp(true)}
                        className="text-blue-600 hover:text-blue-800 transition"
                        title="Learn more about Impact Domains"
                      >
                        <HelpCircle size={16} />
                      </button>
                    </div>
                    <select
                      value={personalImpactFilter || ''}
                      onChange={(e) => {
                        const newFilter = e.target.value || null;
                        setPersonalImpactFilter(newFilter);

                        // Auto-expand categories with matching questions
                        if (newFilter || activityTypeFilter) {
                          const categoriesToExpand = [];
                          finalCategoryKeys.forEach(catKey => {
                            const categoryQuestions = getCategoryQuestions(catKey);
                            const hasMatchingQuestions = categoryQuestions.some(q => {
                              // Check activity type
                              if (activityTypeFilter && q.activity_type !== activityTypeFilter) return false;
                              // Check personal impact domain
                              if (newFilter) {
                                if (newFilter === 'Financial' && !q.impacts_personal_financial) return false;
                                if (newFilter === 'Wellbeing' && !q.impacts_personal_relational) return false;
                                if (newFilter === 'Spiritual' && !q.impacts_personal_spiritual) return false;
                              }
                              return true;
                            });
                            if (hasMatchingQuestions) {
                              categoriesToExpand.push(catKey);
                            }
                          });
                          setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : []);
                        } else {
                          setExpandedCategory([]);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">All Domains</option>
                      <option value="Financial">Financial</option>
                      <option value="Wellbeing">Wellbeing</option>
                      <option value="Spiritual">Spiritual</option>
                    </select>
                  </div>
                </div>

                {/* Active Filters Display */}
                {(activityTypeFilter || personalImpactFilter || selectedGapFilter) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Active Filters:</span>
                    {activityTypeFilter && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                        {activityTypeFilter}
                        <button
                          onClick={() => {
                            setActivityTypeFilter(null);
                            if (!personalImpactFilter && !selectedGapFilter) {
                              setExpandedCategory([]);
                            }
                          }}
                          className="ml-1 hover:text-purple-900"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                    {personalImpactFilter && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                        {personalImpactFilter}
                        <button
                          onClick={() => {
                            setPersonalImpactFilter(null);
                            if (!activityTypeFilter && !selectedGapFilter) {
                              setExpandedCategory([]);
                            }
                          }}
                          className="ml-1 hover:text-amber-900"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                    {selectedGapFilter && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                        {selectedGapFilter === 'n-a' ? 'N/A' : getGapZoneInfo(selectedGapFilter).label}
                        <button
                          onClick={() => setSelectedGapFilter(null)}
                          className="ml-1 hover:text-blue-900"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setActivityTypeFilter(null);
                        setPersonalImpactFilter(null);
                        setSelectedGapFilter(null);
                        setExpandedCategory([]);
                      }}
                      className="text-xs font-medium text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              {finalCategoryKeys.map((catKey) => {
                const score = finalCategoryScores[catKey];
                const categoryName = CATEGORY_DISPLAY_NAMES[catKey] || catKey;
                const categoryQuestions = getFilteredQuestions(catKey);
                const isExpanded = Array.isArray(expandedCategory)
                ? expandedCategory.includes(catKey)
                : expandedCategory === catKey;

                const assessmentType = getAssessmentType(catKey);
                const isFirst = isFirstInGroup(catKey, finalCategoryKeys);
                const isLast = isLastInGroup(catKey, finalCategoryKeys);

                // DEBUG: Log category rendering
                if (isExpanded) {
                  console.log(`Category ${catKey} expanded:`, {
                    categoryName,
                    questionsCount: categoryQuestions.length,
                    allQuestionsCount: allQuestions.length,
                    totalAnswered: answeredQuestions.length,
                    isViewer
                  });
                }

                return (
                  <div key={catKey} className="flex items-stretch gap-0">
                    {/* Left bracket indicator */}
                    <div className="flex flex-col items-center" style={{ width: '40px' }}>
                      {isFirst && (
                        <div className={`text-xs font-semibold px-1 py-0.5 rounded-t ${assessmentType?.color || ''} text-center whitespace-nowrap -rotate-90 origin-center`} style={{ marginTop: '60px', minWidth: '120px' }}>
                          {assessmentType?.label || ''}
                        </div>
                      )}
                      <div className={`flex-1 w-1 ${assessmentType ? 'border-l-4 ' + assessmentType.color.split(' ')[1] : ''}`}></div>
                    </div>

                    {/* Category card */}
                    <div className="flex-1 bg-white rounded-lg border-2 border-gray-300 overflow-hidden shadow-sm">
                      {/* Category Header */}
                      <div className={`w-full p-4 flex items-center justify-between ${getCategoryBackgroundColor(score)}`}>
                      {/* Left side - Expand/Collapse button with category name */}
                      <button
                        onClick={() => toggleCategory(catKey)}
                        className="flex items-center space-x-3 hover:opacity-80 transition flex-1"
                      >
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        <span className="font-semibold text-gray-900">{categoryName}</span>
                      </button>

                      {/* Right side - Help icon, score, and progress bar */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCategoryHelp(catKey);
                          }}
                          className="text-gray-600 hover:text-blue-600 transition p-1"
                          title={`Learn more about ${categoryName}`}
                        >
                          <HelpCircle size={18} />
                        </button>
                        <span className={`text-lg font-bold ${getScoreTextColor(score)}`}>
                          {Math.round(score)}%
                        </span>
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            key={`cat-${catKey}-${categoryAnimationKeys[catKey] || 0}`}
                            className="h-full rounded-full"
                            style={{
                              width: 0,
                              backgroundColor: getScoreColor(score),
                              animation: `growWidth 3500ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                              animationFillMode: 'forwards',
                              '--final-width': `${score}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Questions List */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50">
                        {categoryQuestions.length > 0 ? (
                          <div className="p-3 space-y-1">
                            {categoryQuestions.map((question) => {
                              const answer = getAnswer(question.question_id);
                              return (
                                <div key={question.question_id}>
                                  <div
                                    onClick={() => handleQuestionEdit(question, answer)}
                                    className={`px-3 py-2 rounded border transition cursor-pointer ${
                                      answer && answer.answer_value === 0
                                        ? 'bg-gray-50 border-gray-200 opacity-60'
                                        : viewingHistoricalAssessment
                                        ? 'bg-white border-gray-200 hover:border-amber-400 hover:bg-amber-50'
                                        : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <p className={`text-sm font-medium min-w-[200px] ${
                                        answer && answer.answer_value === 0 ? 'text-gray-400' : 'text-gray-900'
                                      }`}>
                                        {question.subject || 'Question'}
                                      </p>
                                      {answer && (
                                        <>
                                          {answer.answer_value === 0 ? (
                                            // N/A Display
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
                                              N/A
                                            </span>
                                          ) : (
                                            // Normal Score Display - Inline
                                            <>
                                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                <div
                                                  key={`q-${question.question_id}-${categoryAnimationKeys[catKey] || 0}`}
                                                  className="h-full rounded-full"
                                                  style={{
                                                    width: 0,
                                                    backgroundColor: getScoreColor(answer.score),
                                                    animation: `growWidth 3500ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                                                    animationFillMode: 'forwards',
                                                    '--final-width': `${answer.score}%`
                                                  }}
                                                ></div>
                                              </div>
                                              <span className={`text-xs font-semibold whitespace-nowrap min-w-[120px] text-right ${getScoreTextColor(answer.score)}`}>
                                                {answer.score > 86 ? 'No Gaps' :
                                                 answer.score > 72 ? 'Minor Gaps' :
                                                 answer.score > 57 ? 'Considerable Gaps' :
                                                 answer.score > 43 ? 'Critical Gaps' :
                                                 answer.score > 28 ? 'Very Critical' : 'Extremely Critical'}
                                              </span>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {showFullQuestionText && (
                                    <p className={`text-xs mt-1 px-3 ${
                                      answer && answer.answer_value === 0 ? 'text-gray-400 line-through' : 'text-gray-500'
                                    }`}>
                                      {question.question_text}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {selectedGapFilter 
                              ? 'No questions match the selected filter in this category'
                              : 'No questions answered in this category yet'}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT SIDE - Charts */}
            <div className="lg:col-span-2 space-y-6">
              {/* Gap Distribution Pie Chart */}
              <GapDistributionChart
                answeredQuestions={answeredQuestions}
                personalAssessment={personalAssessment}
                hoveredGapZone={hoveredGapZone}
                setHoveredGapZone={setHoveredGapZone}
                selectedGapFilter={selectedGapFilter}
                setSelectedGapFilter={setSelectedGapFilter}
                setExpandedCategory={setExpandedCategory}
                getQuestionsByGapZone={getQuestionsByGapZone}
                getGapZoneInfo={getGapZoneInfo}
                handleGapZoneClick={handleGapZoneClick}
                getCategoryQuestions={getCategoryQuestions}
                finalCategoryKeys={finalCategoryKeys}
              />

              {/* Personal Exit Planning - No Readiness/Attractiveness chart for personal assessments */}
              {/* Chart removed - not applicable to personal assessments */}
            </div>
          </div>
        </div>
      )}

      {/* Scoring Guide Modal */}
      <ScoringGuideModal
        show={showScoringGuide}
        onClose={() => setShowScoringGuide(false)}
        isCollapsed={isScoringGuideCollapsed}
        onToggleCollapse={() => setIsScoringGuideCollapsed(!isScoringGuideCollapsed)}
      />

      {/* Assessment Modal */}
      {showAssessmentModal && (
        <AssessmentModal
          assessmentId={personalAssessment?.id}
          allQuestions={assessmentModalQuestions}
          answeredQuestions={answeredQuestions}
          initialQuestionId={initialQuestionId}
          assessmentType="personal"
          onClose={() => {
            setShowAssessmentModal(false);
            setInitialQuestionId(null);
            loadAssessment();
          }}
          onSave={handleModalSave}
        />
      )}

      {/* Question Editor Modal */}
      {selectedQuestion && (
        <QuestionModal
          question={selectedQuestion}
          assessmentId={personalAssessment?.id}
          onClose={() => setSelectedQuestion(null)}
          onSave={handleQuestionSave}
          isReadOnly={viewingHistoricalAssessment}
          assessmentType="personal"
        />
      )}

      {/* Assessment History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <History className="text-purple-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900">Assessment History</h3>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {assessmentHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Previous Assessments</h3>
                  <p className="text-gray-600">
                    You haven't completed any previous assessments yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assessmentHistory.map((hist) => {
                    const isCurrentlyViewing = personalAssessment && personalAssessment.assessment_id === hist.assessment_id;
                    const completionPercent = hist.total_questions > 0
                      ? (hist.answered_questions / hist.total_questions) * 100
                      : 0;

                    return (
                      <div
                        key={hist.assessment_id}
                        className={`border rounded-lg p-4 transition ${
                          isCurrentlyViewing
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-semibold text-gray-900">
                                Assessment from {new Date(hist.created_at).toLocaleDateString()}
                              </h4>
                              {isCurrentlyViewing && (
                                <span className="px-2 py-1 text-xs font-medium bg-purple-600 text-white rounded">
                                  Currently Viewing
                                </span>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div>
                                <p className="text-sm text-gray-600">Overall Score</p>
                                <p className="text-2xl font-bold text-gray-900">
                                  {Math.round(hist.overall_score)}%
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Completion</p>
                                <p className="text-2xl font-bold text-gray-900">
                                  {Math.round(completionPercent)}%
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span>{hist.answered_questions} of {hist.total_questions} questions answered</span>
                              {hist.updated_at && hist.updated_at !== hist.created_at && (
                                <span>Last updated: {new Date(hist.updated_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>

                          {!isCurrentlyViewing && (
                            <button
                              onClick={() => handleLoadHistoricalAssessment(hist.assessment_id)}
                              className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition whitespace-nowrap"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Type Help Modal */}
      {showActivityTypeHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Activity Type Filter</h2>
              <button
                onClick={() => setShowActivityTypeHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-gray-700 leading-relaxed">
                  Activity types categorize questions based on <strong>priority and strategic function</strong>. Understanding these categories helps you prioritize your personal and exit planning activities to build a strong foundation for your transition.
                </p>
              </div>

              {/* Show scores for each activity type */}
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-gray-900">Your Activity Type Scores</h3>
                {answeredQuestions.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">No answered questions yet. Complete the assessment to see your scores.</p>
                  </div>
                ) : (
                  Object.keys(activityTypeScores).map(activityType => {
                    const score = activityTypeScores[activityType] || 0;
                    const scoreColor = getScoreColor(score);
                    const darkColor = getScoreDarkColor(score);
                    return (
                      <div key={activityType} className="flex items-center justify-between p-3 rounded-lg border-2" style={{ backgroundColor: `${scoreColor}60`, borderColor: darkColor }}>
                        <span className="font-medium" style={{ color: darkColor }}>{activityType}</span>
                        <span className="text-xl font-bold" style={{ color: darkColor }}>{Math.round(score)}%</span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">De-Risking Activity</h3>
                  <p className="text-sm text-gray-700">Questions that address potential risks to your successful exit, including financial vulnerabilities, health concerns, relationship issues, and lack of planning. These activities should typically be completed first as they prevent problems that could derail your transition.</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Strategy Activity</h3>
                  <p className="text-sm text-gray-700">Questions related to long-term vision, exit planning, legacy goals, and life purpose. These define the direction and meaning of your transition and should guide your other planning activities.</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Efficiency Activity</h3>
                  <p className="text-sm text-gray-700">Questions about optimizing your current situation, improving systems and processes, and making better use of resources. These activities help you prepare more effectively and avoid wasting time or money.</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Growth Activity</h3>
                  <p className="text-sm text-gray-700">Questions focused on developing new capabilities, expanding relationships, building spiritual depth, and increasing financial security. These activities strengthen your foundation for a successful transition.</p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Culture Activity</h3>
                  <p className="text-sm text-gray-700">Questions about values, character, family dynamics, and the relational environment you're creating. These shape the quality of your relationships and the legacy you'll leave behind.</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowActivityTypeHelp(false)}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impact Domain (Financial, Wellbeing, Spiritual) Help Modal */}
      {showImpactTypeHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Impact Domain Filter</h2>
              <button
                onClick={() => setShowImpactTypeHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-gray-700 leading-relaxed">
                  The Impact Domain filter helps you focus on specific areas of your personal and exit planning readiness. Each domain represents a critical aspect of preparing for a successful business transition and life after exit.
                </p>
              </div>

              {/* Show scores for each impact domain */}
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-gray-900">Your Impact Domain Scores</h3>
                {answeredQuestions.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">No answered questions yet. Complete the assessment to see your scores.</p>
                  </div>
                ) : (
                  IMPACT_DOMAINS.map(({ key: domain }) => {
                    const score = impactDomainScores[domain] || 0;
                    const scoreColor = getScoreColor(score);
                    const darkColor = getScoreDarkColor(score);
                    return (
                      <div key={domain} className="flex items-center justify-between p-3 rounded-lg border-2" style={{ backgroundColor: `${scoreColor}60`, borderColor: darkColor }}>
                        <span className="font-medium" style={{ color: darkColor }}>{domain}</span>
                        <span className="text-xl font-bold" style={{ color: darkColor }}>{Math.round(score)}%</span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Financial</h3>
                  <p className="text-sm text-gray-700">
                    Questions that assess your personal financial readiness for exit, including wealth management, estate planning, tax planning, insurance coverage, liquidity needs, and overall financial security. These questions help ensure you have the financial foundation necessary for a successful transition.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Wellbeing</h3>
                  <p className="text-sm text-gray-700">
                    Questions that evaluate your relational and emotional preparedness for life after exit. This includes family relationships, marriage quality, social connections, physical health, emotional/mental health, hobbies and interests, and life purpose beyond business. Strong wellbeing is essential for a fulfilling post-exit life.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Spiritual</h3>
                  <p className="text-sm text-gray-700">
                    Questions that explore your spiritual foundation, biblical wisdom application, character and integrity, eternal perspective, and legacy planning from a faith-based viewpoint. These questions help ensure your exit aligns with your values and creates lasting Kingdom impact.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <h3 className="font-bold text-amber-900 mb-2">A Holistic Approach</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  True exit readiness requires attention to all three domains. Financial preparation without relational health can lead to isolation. Spiritual clarity without financial planning can create unnecessary stress. A balanced approach across Financial, Wellbeing, and Spiritual domains creates the foundation for a successful and fulfilling transition.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowImpactTypeHelp(false)}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Summary Modal */}
      <AssessmentSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        loading={loadingSummary}
        summaryData={summaryData}
      />

      {/* Start Assessment Modal */}
      <StartAssessmentModal
        show={showStartModal}
        onClose={() => setShowStartModal(false)}
        selectedTypes={selectedAssessmentTypes}
        onToggleType={toggleAssessmentType}
        onStart={handleConfirmStartAssessment}
      />

      {/* Category Help Modal */}
      <CategoryHelpModal
        show={!!showCategoryHelp}
        categoryKey={showCategoryHelp}
        onClose={() => setShowCategoryHelp(null)}
      />

      {/* Personal Scoring Criteria Modal (Clear, Documented, Transferable) */}
      <PersonalScoringCriteriaModal
        isOpen={showCDTHelp}
        onClose={() => setShowCDTHelp(false)}
      />
    </div>
  );
};

export default PersonalGapAnalysis;